import {
  Button,
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  LoadingSpinner,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { useCallback, useMemo } from 'react';
import { cn } from '@ais-ui/utils';
import { PiAnchorThin, PiArrowSquareOut, PiBoat, PiPath } from 'react-icons/pi';
import { Port } from '../AISMapContext';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { PortList } from '@ais-ui/api-spec/portserver';
import { fromLonLat } from 'ol/proj';
import { Point } from 'ol/geom';
import {
  PortSelectionMode,
  useSelectionState,
} from '../../../components/SelectionStateProvider';
import { usePermissions } from '../../Permissions/PermissionsProvider';
import { usePortsFilter } from '../../Settings/Port';
import { useLocalization } from '@ais-ui/locale-management';
import { openPopupOnSecondaryScreen, PopupType } from '@ais-ui/popups';
import {
  RecentSearch,
  useRecentSearches,
} from '../../../components/RecentSearchesProvider';
import { RecentPortSearches } from './RecentPortSearches';

interface PortSearchProps {
  searchString: string;
  setSearchString: React.Dispatch<React.SetStateAction<string>>;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
}

export const PortSearch = ({
  searchString,
  setSearchString,
  setIsFocused,
}: PortSearchProps) => {
  const { messages } = useLocalization();
  const { map } = useOpenLayersContext();
  const { setSelectedPorts, setPortSelectionMode } = useSelectionState();
  const { addRecentSearch } = useRecentSearches();
  const permissions = usePermissions();

  const { filteredPorts, isLoading } = usePortsFilter({
    searchString,
    maxResults: 150,
  });

  const scrollAreaHeightClass = useMemo(() => {
    const itemHeight = 52; // Height for each item in pixels
    const maxHeight = 256; // Maximum allowed height in pixels

    if (!filteredPorts || filteredPorts.length === 0) return '';

    const totalHeight = Math.min(filteredPorts.length * itemHeight, maxHeight);

    return `h-[${totalHeight}px]`;
  }, [filteredPorts]);

  const selectPort = useCallback(
    (port: PortList, mode: PortSelectionMode) => {
      setSearchString(port.name);
      setPortSelectionMode(mode);

      // Add to recent searches (only if we have valid coordinates)
      const coords = port.position.coordinates as [number, number];
      if (coords[0] !== 0 || coords[1] !== 0) {
        addRecentSearch({
          id: port.id.toString(),
          name: port.name,
          type: 'port',
          metadata: {
            locode: port.locode,
            country: port.country,
            coordinates: coords,
          },
        });
      }

      const selectedPort: Port = {
        id: port.id.toString(),
        name: port.name,
        country: port.country,
        locode: port.locode,
        geometry: new Point(
          fromLonLat(port.position.coordinates as [number, number]),
        ),
      };
      setSelectedPorts([selectedPort]);

      const [longitude, latitude] = port.position.coordinates as [
        number,
        number,
      ];
      const view = map.getView();

      // Transform coordinates from [lon, lat] to map projection
      const transformedPosition = fromLonLat([longitude, latitude]);

      // Set the map's center and zoom level
      view.setCenter(transformedPosition);
      view.setZoom(12);

      // Close the popover after selecting a port
      setTimeout(() => {
        setIsFocused(false);
      }, 300);
    },
    [
      addRecentSearch,
      map,
      setIsFocused,
      setPortSelectionMode,
      setSearchString,
      setSelectedPorts,
    ],
  );

  const onActionClick = useCallback(
    (e: React.MouseEvent, port: PortList, mode: PortSelectionMode) => {
      e.stopPropagation();
      e.preventDefault();
      selectPort(port, mode);
    },
    [selectPort],
  );

  // Open port timeline directly in a popup window
  const onTimelinePopupClick = useCallback(
    (e: React.MouseEvent, port: PortList) => {
      e.stopPropagation();
      e.preventDefault();

      const params: Record<string, string | number> = {};
      params.portId = port.id;
      params.portName = port.name;
      params.portLocode = port.locode;
      params.activeTab = 'timeline';

      openPopupOnSecondaryScreen(PopupType.Routes, params);

      // Close the search popover
      setTimeout(() => {
        setIsFocused(false);
      }, 300);
    },
    [setIsFocused],
  );

  // Default click on row uses routes if permitted, otherwise nextport
  const onRowClick = useCallback(
    (port: PortList) => {
      // Default to routes if permitted, otherwise nextport
      const mode: PortSelectionMode = permissions.port ? 'routes' : 'nextport';

      selectPort(port, mode);
    },
    [permissions.port, selectPort],
  );

  // Handle recent search click - convert RecentSearch to PortList and use selectPort
  const onRecentSearchClick = useCallback(
    (search: RecentSearch) => {
      const portList: PortList = {
        id: parseInt(search.id, 10),
        name: search.name,
        locode: search.metadata.locode || '',
        country: search.metadata.country || '',
        position: {
          type: 'Point',
          coordinates: search.metadata.coordinates || [0, 0],
        },
      };
      onRowClick(portList);
    },
    [onRowClick],
  );

  return (
    <Command className="h-fit">
      <CommandList className="h-fit ">
        {searchString.length >= 3 && filteredPorts.length > 0 && (
          <CommandGroup
            heading={`ports (${filteredPorts.length})`}
          ></CommandGroup>
        )}
        {!isLoading && searchString.length < 3 && (
          <RecentPortSearches onRecentSearchClick={onRecentSearchClick} />
        )}
        {!isLoading &&
          searchString.length >= 3 &&
          filteredPorts.length === 0 && (
            <div className="py-6 text-center text-sm">
              {messages.portSelectNoResults}
            </div>
          )}
        {isLoading && <LoadingSpinner size="small"></LoadingSpinner>}
        {searchString.length >= 3 && filteredPorts.length > 0 && (
          <ScrollArea className={cn('h-[256px]', scrollAreaHeightClass)}>
            {filteredPorts.map((port, index) => (
              <CommandItem
                className="mr-4 py-2 hover:cursor-pointer hover:bg-transparent data-[selected=true]:bg-transparent group border-b border-border last:border-b-0"
                onSelect={() => onRowClick(port)}
                key={index}
              >
                <div className="flex flex-col space-y-1 w-full">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-1">
                      <PiAnchorThin className={cn('mr-2 size-4')} />
                      <span className="group-hover:underline">{port.name}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              'h-6 w-6',
                              !permissions.port &&
                                'opacity-50 cursor-not-allowed',
                            )}
                            onClick={(e) => onActionClick(e, port, 'routes')}
                            disabled={!permissions.port}
                          >
                            <PiPath className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {permissions.port
                            ? 'Show routes'
                            : 'Routes permission required'}
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              'h-6 w-6',
                              !permissions.nextport &&
                                'opacity-50 cursor-not-allowed',
                            )}
                            onClick={(e) => onActionClick(e, port, 'nextport')}
                            disabled={!permissions.nextport}
                          >
                            <PiBoat className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {permissions.nextport
                            ? 'Show NextPort arrivals'
                            : 'NextPort permission required'}
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              'h-6 w-6',
                              !permissions.port &&
                                'opacity-50 cursor-not-allowed',
                            )}
                            onClick={(e) => onTimelinePopupClick(e, port)}
                            disabled={!permissions.port}
                          >
                            <PiArrowSquareOut className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {permissions.port
                            ? 'Open port call timeline in popup'
                            : 'Routes permission required'}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="flex space-x-2 text-xs text-muted-foreground items-center">
                    <div>Locode: {port.locode}</div>
                    <div
                      className={cn('rounded-full h-1 w-1 bg-blue-600')}
                    ></div>
                    <div>Country: {port.country}</div>
                  </div>
                </div>
              </CommandItem>
            ))}
          </ScrollArea>
        )}
      </CommandList>
    </Command>
  );
};
